<template>
  <div>

    <!-- DASHBOARD WIDGET -->
    <v-card v-if="compact" flat outlined>
      <div class="px-4 pt-3 pb-1 d-flex align-center justify-space-between">
        <span class="subtitle-2 font-weight-medium">{{ title }}</span>
        <v-btn text x-small color="primary" @click="openDialog">
          View all
          <v-icon x-small right>mdi-arrow-right</v-icon>
        </v-btn>
      </div>
      <v-divider />

      <div v-if="loading" class="pa-3">
        <v-skeleton-loader v-for="n in 5" :key="n" type="list-item-two-line" class="mb-1" />
      </div>
      <div v-else-if="!items.length" class="pa-4 caption grey--text text-center">
        No recent activity.
      </div>
      <v-list v-else dense class="py-0">
        <v-list-item
          v-for="(a, i) in items"
          :key="a._id || i"
          dense
          style="min-height:52px"
        >
          <v-list-item-icon class="mr-2 mt-3">
            <v-icon small :color="actionColor(a.action)">{{ actionIcon(a.action) }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="body-2 font-weight-medium">
              {{ friendlyTitle(a) }}
              <v-chip x-small :color="actionColor(a.action)" dark class="ml-1" style="font-size:10px">
                {{ a.action }}
              </v-chip>
            </v-list-item-title>
            <v-list-item-subtitle class="body-2">
              {{ capitalize(friendlyDescription(a)) }}
            </v-list-item-subtitle>
            <v-list-item-subtitle class="caption grey--text">
              {{ capitalizeName(a.actor && a.actor.username) }} · {{ timeAgo(a.date || a.createdAt) }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-card>

    <!-- ══ FULL / TAB MODE ══ -->
    <div v-else>

      <!-- Filter toolbar row -->
      <div class="d-flex align-center justify-space-between mb-3">
        <span v-if="title" class="subtitle-1 font-weight-medium">{{ title }}</span>
        <div class="d-flex align-center" style="gap:8px">
          <v-chip
            v-if="hasActiveFilters"
            x-small close outlined color="primary"
            @click:close="clearFilters"
          >
            <v-icon x-small left>mdi-filter</v-icon>
            Filters active
          </v-chip>
          <v-btn depressed small @click="filterDialog = true">
            <v-icon small left>mdi-filter-outline</v-icon>
            Filters
          </v-btn>
          <v-btn
            depressed small icon
            :loading="loading"
            :disabled="loading"
            title="Refresh"
            @click="fetchPage"
          >
            <v-icon small>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>

      <v-data-table
        :headers="tableHeaders"
        :items="items"
        :loading="loading"
        :items-per-page="limit"
        :footer-props="{ itemsPerPageOptions: [5, 10], itemsPerPageText: 'Rows per page' }"
        loading-text="Loading activity..."
        no-data-text="No activity found"
        class="elevation-1"
      >
        <!-- Type chip -->
        <template v-slot:item.action="{ item }">
          <v-chip x-small :color="actionColor(item.action)" dark style="font-size:10px">
            <v-icon x-small left>{{ actionIcon(item.action) }}</v-icon>
            {{ item.action }}
          </v-chip>
        </template>

        <!-- Activity description -->
        <template v-slot:item.eventMessage="{ item }">
          <div class="py-1">
            <div class="body-2 font-weight-medium">{{ capitalize(friendlyTitle(item)) }}</div>
            <div class="caption grey--text">{{ capitalize(friendlyDescription(item)) }}</div>
          </div>
        </template>

        <!-- Status chip -->
        <template v-slot:item.status="{ item }">
          <v-chip
            x-small
            :color="item.status === 'Success' ? 'green' : 'red'"
            outlined
            style="font-size:10px"
          >
            {{ item.status }}
          </v-chip>
        </template>

        <!-- Actor -->
        <template v-slot:item.actor="{ item }">
          <span class="body-2">{{ capitalizeName(item.actor && item.actor.username) }}</span>
        </template>

        <!-- Date -->
        <template v-slot:item.date="{ item }">
          <div>
            <div class="body-2">{{ timeAgo(item.date || item.createdAt) }}</div>
            <div class="caption grey--text">{{ fmtDate(item.date || item.createdAt) }}</div>
          </div>
        </template>

        <!-- View case -->
        <template v-if="!caseId" v-slot:item.viewCase="{ item }">
          <v-chip
            v-if="item.identifier && caseExists(item.identifier)"
            x-small outlined color="primary"
            style="cursor:pointer;font-size:10px"
            @click="$emit('go-to-case', item.identifier)"
          >
            {{ caseRef(item.identifier) }}
            <v-icon x-small right>mdi-arrow-right</v-icon>
          </v-chip>
        </template>
      </v-data-table>
    </div>

    <!-- ══ FILTER MODAL ══ -->
    <v-dialog v-model="filterDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon left color="primary">mdi-filter-outline</v-icon>
            <span class="subtitle-1">Filter activity</span>
          </div>
          <v-btn icon @click="filterDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-select
            v-model="filters.action"
            :items="actionFilterOptions"
            label="Activity type"
            clearable dense outlined
            prepend-inner-icon="mdi-tag-outline"
            class="mb-3"
          />
          <v-text-field
            v-model="filters.from"
            label="Date from"
            type="date"
            dense outlined
            prepend-inner-icon="mdi-calendar-start"
            class="mb-3"
          />
          <v-text-field
            v-model="filters.to"
            label="Date to"
            type="date"
            dense outlined
            prepend-inner-icon="mdi-calendar-end"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn text color="grey darken-1" :disabled="!hasActiveFilters" @click="clearFilters">
            <v-icon left small>mdi-close</v-icon>Clear all
          </v-btn>
          <v-spacer />
          <v-btn text @click="filterDialog = false">Cancel</v-btn>
          <v-btn depressed color="primary" :loading="loading" @click="applyFiltersAndClose">
            <v-icon left small>mdi-magnify</v-icon>Apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ══ FILTER MODAL ══ -->
    <v-dialog v-model="dFilterDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon left color="primary">mdi-filter-outline</v-icon>
            <span class="subtitle-1">Filter activity</span>
          </div>
          <v-btn icon @click="dFilterDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-select
            v-model="dFilters.action"
            :items="actionFilterOptions"
            label="Activity type"
            clearable dense outlined
            prepend-inner-icon="mdi-tag-outline"
            class="mb-3"
          />
          <v-text-field
            v-model="dFilters.from"
            label="Date from"
            type="date"
            dense outlined
            prepend-inner-icon="mdi-calendar-start"
            class="mb-3"
          />
          <v-text-field
            v-model="dFilters.to"
            label="Date to"
            type="date"
            dense outlined
            prepend-inner-icon="mdi-calendar-end"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn text color="grey darken-1" :disabled="!hasActiveDFilters" @click="clearDFilters">
            <v-icon left small>mdi-close</v-icon>Clear all
          </v-btn>
          <v-spacer />
          <v-btn text @click="dFilterDialog = false">Cancel</v-btn>
          <v-btn depressed color="primary" :loading="dLoading" @click="applyDFiltersAndClose">
            <v-icon left small>mdi-magnify</v-icon>Apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ══ VIEW ALL DIALOG ══ -->
    <v-dialog v-model="dialog" max-width="960" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon left color="primary">mdi-chart-timeline</v-icon>
            <span>{{ title }}</span>
          </div>
          <v-btn icon @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4">

          <!-- Dialog filter toolbar -->
          <div class="d-flex align-center justify-end mb-3" style="gap:8px">
            <v-chip
              v-if="hasActiveDFilters"
              x-small close outlined color="primary"
              @click:close="clearDFilters"
            >
              <v-icon x-small left>mdi-filter</v-icon>
              Filters active
            </v-chip>
            <v-btn depressed small @click="dFilterDialog = true">
              <v-icon small left>mdi-filter-outline</v-icon>
              Filters
            </v-btn>
          </div>

          <!-- Dialog table -->
          <v-data-table
            :headers="tableHeaders"
            :items="dItems"
            :loading="dLoading"
            :items-per-page="dLimit"
            :footer-props="{ itemsPerPageOptions: [5, 10], itemsPerPageText: 'Rows per page' }"
            loading-text="Loading activity..."
            no-data-text="No activity found"
            class="elevation-1"
          >
            <template v-slot:item.action="{ item }">
              <v-chip x-small :color="actionColor(item.action)" dark style="font-size:10px">
                <v-icon x-small left>{{ actionIcon(item.action) }}</v-icon>
                {{ item.action }}
              </v-chip>
            </template>
            <template v-slot:item.eventMessage="{ item }">
              <div class="py-1">
                <div class="caption font-weight-medium">{{ friendlyTitle(item) }}</div>
                <div class="caption grey--text">{{ friendlyDescription(item) }}</div>
              </div>
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip x-small :color="item.status === 'Success' ? 'green' : 'red'" outlined style="font-size:10px">
                {{ item.status }}
              </v-chip>
            </template>
            <template v-slot:item.actor="{ item }">
              <span class="body-2">{{ capitalizeName(item.actor && item.actor.username) }}</span>
            </template>
            <template v-slot:item.date="{ item }">
              <div>
                <div class="body-2">{{ timeAgo(item.date || item.createdAt) }}</div>
                <div class="caption grey--text">{{ fmtDate(item.date || item.createdAt) }}</div>
              </div>
            </template>
            <template v-if="!caseId" v-slot:item.viewCase="{ item }">
              <v-chip
                v-if="item.identifier && caseExists(item.identifier)"
                x-small outlined color="primary"
                style="cursor:pointer;font-size:10px"
                @click="onDialogGoToCase(item.identifier)"
              >
                {{ caseRef(item.identifier) }}
                <v-icon x-small right>mdi-arrow-right</v-icon>
              </v-chip>
            </template>
          </v-data-table>

        </v-card-text>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import moment from 'moment';
import { AmlCaseService } from '@/modules/amlpro/services/aml-case.service';
import FormatMixin from '@/mixins/format.mixin';

const svc = new AmlCaseService();

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  return moment(dateStr).fromNow();
}

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  return moment(dateStr).format('MMM D, YYYY h:mm A');
}

// Action display map 

const ACTION_META = {
  CREATE:       { icon: 'mdi-plus-circle-outline',    color: 'green'  },
  UPDATE:       { icon: 'mdi-pencil-circle-outline',  color: 'blue'   },
  DELETE:       { icon: 'mdi-minus-circle-outline',   color: 'red'    },
  Assignment:   { icon: 'mdi-account-arrow-right',    color: 'teal'   },
  Unassignment: { icon: 'mdi-account-remove-outline', color: 'orange' },
  Export:       { icon: 'mdi-export',                 color: 'purple' },
};

const LABEL_MAP = {
  'Open AML case': {
    title: 'Case opened',
    describe: (a) => {
      const ref = a._caseRef;
      return ref ? `Opened case ${ref}` : 'A new AML case was opened';
    },
  },
  'Update AML case': {
    title: 'Case updated',
    describe: (a) => {
      const ref  = a._caseRef;
      const obj  = a.eventObject || {};
      const bits = [];
      if (obj.status)      bits.push(`status → ${obj.status.replace(/_/g, ' ')}`);
      if (obj.priority)    bits.push(`priority → ${obj.priority}`);
      if (obj.reportStage) bits.push(`stage → ${obj.reportStage}`);
      if (obj.summary)     bits.push('summary updated');
      const detail = bits.length ? bits.join(', ') : 'details updated';
      return ref ? `${ref}: ${detail}` : detail;
    },
  },
  'Close AML case': {
    title: 'Case closed',
    describe: (a) => {
      const ref    = a._caseRef;
      const obj    = a.eventObject || {};
      const status = obj.status ? obj.status.replace(/_/g, ' ') : '';
      const reason = obj.closeReason ? ` — ${obj.closeReason}` : '';
      return ref
        ? `${ref} closed as ${status}${reason}`
        : `Closed as ${status}${reason}`;
    },
  },
  'Assign AML case': {
    title: 'Case assigned',
    describe: (a) => {
      const ref = a._caseRef;
      return ref ? `${ref} assigned to a new officer` : 'Case assigned to a new officer';
    },
  },
  'Deassign AML case': {
    title: 'Case unassigned',
    describe: (a) => {
      const ref = a._caseRef;
      return ref ? `${ref} was unassigned` : 'Case was unassigned';
    },
  },
  'Add comment to AML case': {
    title: 'Comment added',
    describe: (a) => {
      const ref  = a._caseRef;
      const type = a.eventObject?.type ? ` (${a.eventObject.type})` : '';
      return ref ? `Comment added to ${ref}${type}` : `Comment added${type}`;
    },
  },
  'Link transaction to AML case': {
    title: 'Transaction linked',
    describe: (a) => {
      const ref = a._caseRef;
      return ref ? `Transaction linked to ${ref}` : 'Transaction linked to case';
    },
  },
  'Unlink transaction from AML case': {
    title: 'Transaction unlinked',
    describe: (a) => {
      const ref = a._caseRef;
      return ref ? `Transaction removed from ${ref}` : 'Transaction removed from case';
    },
  },
  'Record AML case action': {
    title: 'Action recorded',
    describe: (a) => {
      const ref    = a._caseRef;
      const action = a.eventObject?.action;
      const stage  = a.eventObject?.stage;
      if (action && stage) return ref ? `"${action}" added to ${ref} (${stage})` : `"${action}" added (${stage})`;
      if (action)          return ref ? `"${action}" added to ${ref}` : `"${action}" recorded`;
      return ref ? `New action recorded on ${ref}` : 'New action recorded';
    },
  },
  'Update AML case action': {
    title: 'Action updated',
    describe: (a) => {
      const ref      = a._caseRef;
      const obj      = a.eventObject || {};
      const action   = obj.action;
      if (obj.complete === true)  return action
        ? `"${action}" marked complete${ref ? ` on ${ref}` : ''}`
        : `Action marked complete${ref ? ` on ${ref}` : ''}`;
      if (obj.complete === false) {
        // Check if it's a waive (comment starts with WAIVED:)
        if (obj.comment && String(obj.comment).includes('WAIVED:')) {
          return action
            ? `"${action}" waived${ref ? ` on ${ref}` : ''}`
            : `Action waived${ref ? ` on ${ref}` : ''}`;
        }
        return action
          ? `"${action}" marked incomplete${ref ? ` on ${ref}` : ''}`
          : `Action marked incomplete${ref ? ` on ${ref}` : ''}`;
      }
      return action
        ? `"${action}" updated${ref ? ` on ${ref}` : ''}`
        : `Action updated${ref ? ` on ${ref}` : ''}`;
    },
  },
};

export default {
  name: 'AmlActivityFeed',
  mixins: [FormatMixin],

  props: {
    caseId:  { type: String,  default: null  },
    title:   { type: String,  default: 'Recent activity' },
    compact: { type: Boolean, default: false },
    limit:   { type: Number,  default: 10   },
  },

  data() {
    return {
      fetchLimit: 100,
      items:    [],
      total:    0,
      loading:  true,
      filters:  { action: null, from: null, to: null },
      dialog:        false,
      filterDialog:  false,
      dFilterDialog: false,
      dItems:   [],
      dLoading: false,
      dLimit:   10,
      dFilters: { action: null, from: null, to: null },
      caseMap: {},

      actionFilterOptions: [
        { text: 'Create',       value: 'CREATE'       },
        { text: 'Update',       value: 'UPDATE'       },
        { text: 'Delete',       value: 'DELETE'       },
        { text: 'Assignment',   value: 'Assignment'   },
        { text: 'Unassignment', value: 'Unassignment' },
      ],
    };
  },

  computed: {
    hasActiveFilters() {
      return !!(this.filters.action || this.filters.from || this.filters.to);
    },
    hasActiveDFilters() {
      return !!(this.dFilters.action || this.dFilters.from || this.dFilters.to);
    },
    tableHeaders() {
      const h = [
        { text: 'Type',     value: 'action',        sortable: false, width: '120px'},
        { text: 'Activity', value: 'eventMessage',  sortable: false                },
        { text: 'Status',   value: 'status',        sortable: false, width: '90px' },
        { text: 'By',       value: 'actor',         sortable: false, width: '250px'},
        { text: 'When',     value: 'date',          sortable: false, width: '170px'},
      ];
      if (!this.caseId) {
        h.push({ text: 'Case', value: 'viewCase', sortable: false, width: '120px' });
      }
      return h;
    },
  },

  mounted() {
    this.fetchPage();
  },

  watch: {
    caseId() { this.fetchPage(); },
    dialog(val) {
      if (val) {
        this.dFilters = { action: null, from: null, to: null };
        this.dItems   = [];
        this.fetchDPage();
      }
    },
  },

  methods: {
    async fetchPage() {
      this.loading = true;
      try {
        const params = { limit: this.compact ? 5 : this.fetchLimit };
        if (this.filters.action) params.action = this.filters.action;
        if (this.filters.from)   params.from   = this.filters.from;
        if (this.filters.to)     params.to     = this.filters.to;

        const res  = this.caseId
          ? await svc.fetchCaseActivity(this.caseId, params)
          : await svc.fetchActivity(params);

        const data = res.data ?? res;
        const rows = data.data ?? data.audits ?? [];
        this.total = data.total ?? 0;

        await this.hydrateCaseRefs(rows);
        this.items = rows;
      } catch (e) {
        console.error('[AmlActivityFeed] fetchPage error:', e);
        this.items = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },

    async fetchDPage() {
      this.dLoading = true;
      try {
        const params = { limit: this.fetchLimit };
        if (this.dFilters.action) params.action = this.dFilters.action;
        if (this.dFilters.from)   params.from   = this.dFilters.from;
        if (this.dFilters.to)     params.to     = this.dFilters.to;

        const res  = this.caseId
          ? await svc.fetchCaseActivity(this.caseId, params)
          : await svc.fetchActivity(params);

        const data = res.data ?? res;
        const rows = data.data ?? data.audits ?? [];

        await this.hydrateCaseRefs(rows);
        this.dItems = rows;
      } catch (e) {
        console.error('[AmlActivityFeed] fetchDPage error:', e);
        this.dItems = [];
      } finally {
        this.dLoading = false;
      }
    },

    applyFilters() { this.fetchPage(); },
    applyFiltersAndClose() { this.filterDialog = false; this.fetchPage(); },

    clearFilters() {
      this.filters = { action: null, from: null, to: null };
      this.fetchPage();
    },
    applyDFilters() { this.fetchDPage(); },
    applyDFiltersAndClose() { this.dFilterDialog = false; this.fetchDPage(); },

    clearDFilters() {
      this.dFilters = { action: null, from: null, to: null };
      this.fetchDPage();
    },

    async hydrateCaseRefs(rows) {
      if (this.caseId) return;

      const uuids = [...new Set(
        rows
          .map(r => r.identifier)
          .filter(id => id && !this.caseMap[id]),
      )];

      await Promise.all(uuids.map(async (id) => {
        try {
          const res  = await svc.fetchOne(id);
          const data = res.data ?? res;
          this.$set(this.caseMap, id, {
            ref:    data.caseReference ?? id,
            exists: true,
          });
        } catch {
          this.$set(this.caseMap, id, { ref: null, exists: false });
        }
      }));

      for (const row of rows) {
        row._caseRef = this.caseMap[row.identifier]?.ref ?? null;
      }
    },

    caseExists(id) { return !!this.caseMap[id]?.exists; },
    caseRef(id)    { return this.caseMap[id]?.ref ?? null; },

    //display helpers
    friendlyTitle(entry) {
      return LABEL_MAP[entry.label]?.title ?? entry.label ?? '—';
    },

    friendlyDescription(entry) {
      const mapper = LABEL_MAP[entry.label];
      if (mapper) return mapper.describe(entry);
      let msg = entry.eventMessage ?? '—';
      const uuidRe = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
      if (entry._caseRef) {
        msg = msg.replace(new RegExp(entry.identifier, 'gi'), entry._caseRef);
      }
      return msg.replace(uuidRe, '').replace(/\s{2,}/g, ' ').trim();
    },

    capitalize(str) { return str ? str.charAt(0).toUpperCase() + str.slice(1) : ''; },
    actionIcon(action)  { return ACTION_META[action]?.icon  ?? 'mdi-information-outline'; },
    actionColor(action) { return ACTION_META[action]?.color ?? 'grey'; },

    openDialog() { this.dialog = true; },

    onDialogGoToCase(id) {
      this.dialog = false;
      this.$emit('go-to-case', id);
    },

    timeAgo,
    fmtDate,
  },
};
</script>

<style scoped>
.v-data-table >>> tbody tr:hover {
  background: #f5f5f5 !important;
}
</style>